import { useState } from "react";
import { ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export type MultiComboboxOption = { value: string; label: string; hint?: string };

export function multiComboboxLabel(value: string[], options: MultiComboboxOption[]): string {
  if (value.length === 0) return "All";
  if (value.length === 1) return options.find((option) => option.value === value[0])?.label ?? value[0];
  return `${value.length} selected`;
}

export function toggleMultiComboboxValue(value: string[], next: string): string[] {
  return value.includes(next) ? value.filter((item) => item !== next) : [...value, next];
}

export function MultiCombobox({ value, onChange, options, placeholder, emptyText, ariaLabel, disabled = false, nonMutating = false }: {
  value: string[];
  onChange: (value: string[]) => void;
  options: MultiComboboxOption[];
  placeholder: string;
  emptyText: string;
  ariaLabel?: string;
  disabled?: boolean;
  nonMutating?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return <Popover open={open} onOpenChange={setOpen}>
    <PopoverTrigger asChild>
      <Button type="button" data-nonmutating={nonMutating || undefined} variant="outline" role="combobox" aria-label={ariaLabel} aria-expanded={open} disabled={disabled} className="w-full justify-between font-normal">
        <span className="truncate">{multiComboboxLabel(value, options)}</span><ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
      <Command>
        <CommandInput data-nonmutating={nonMutating || undefined} placeholder={placeholder} />
        <CommandList><CommandEmpty>{emptyText}</CommandEmpty><CommandGroup>
          <CommandItem value="Clear" onSelect={() => onChange([])}>
            <span className="w-4" aria-hidden />
            <span>Clear</span>
          </CommandItem>
          {options.map((option) => {
            const checked = value.includes(option.value);
            return <CommandItem key={option.value} value={`${option.label} ${option.hint ?? ""}`} onSelect={() => onChange(toggleMultiComboboxValue(value, option.value))}>
              <Checkbox data-nonmutating={nonMutating || undefined} checked={checked} tabIndex={-1} aria-hidden className="pointer-events-none" />
              <span className="min-w-0"><span className="block truncate">{option.label}</span>{option.hint && <span className="block truncate text-xs text-muted-foreground">{option.hint}</span>}</span>
            </CommandItem>;
          })}
        </CommandGroup></CommandList>
      </Command>
    </PopoverContent>
  </Popover>;
}
